(() => {
  /* Vertaalde teksten. layout/theme.liquid zet ze als data-attribuut op de
     script-tag van dit bestand; document.currentScript bestaat alleen
     terwijl dit script voor het eerst draait, dus meteen uitlezen. Het
     Nederlands blijft staan als vangnet voor als de attributen ontbreken.
     In de praktijk zijn het zelf ook vangnetten: Horizon geeft de twee
     prijsvelden al een vertaald aria-label mee, en dat gaat voor. */
  const bron =
    document.currentScript || document.querySelector('script[data-el-lage-prijs]');
  const TEKST = {
    lagePrijs: bron?.dataset.elLagePrijs || 'Lage prijs',
    hogePrijs: bron?.dataset.elHogePrijs || 'Hoge prijs',
  };

  const JA = new Set(['ja', 'yes', 'oui', 'true', 'waar']);
  const NEE = new Set(['nee', 'neen', 'no', 'non', 'false', 'onwaar']);

  function naarGetal(waarde) {
    const tekst = String(waarde ?? '').trim();
    if (!tekst) return NaN;
    const genormaliseerd = tekst.includes(',')
      ? tekst.replace(/\./g, '').replace(',', '.')
      : tekst;
    return parseFloat(genormaliseerd.replace(/[^0-9.-]/g, ''));
  }

  function naarTekst(getal, komma) {
    const vast = (Math.round(getal * 100) / 100).toFixed(2);
    return komma ? vast.replace('.', ',') : vast;
  }

  /* Wat er in het veld komt en dus in de URL belandt. Shopify negeert een
     bedrag met een komma stilzwijgend: filter.v.price.lte=5,00 geeft gewoon
     alle producten terug. Altijd een punt, zonder nullen achteraan. */
  function naarVeldWaarde(getal) {
    return String(Math.round(getal * 100) / 100);
  }

  function stapVoor(bereik) {
    if (bereik <= 20) return 0.1;
    if (bereik <= 100) return 1;
    if (bereik <= 1000) return 5;
    return 10;
  }

  /* --- Ja/nee-filters samenvouwen ---------------------------------------- */

  /* Alleen schrijven als er iets verandert. Een attribuut op dezelfde waarde
     zetten telt ook als mutatie, en met de observer hieronder houdt dat
     zichzelf eindeloos aan de gang. */
  function zetAttr(el, naam, waarde) {
    if (waarde === null) {
      if (el.hasAttribute(naam)) el.removeAttribute(naam);
    } else if (el.getAttribute(naam) !== waarde) {
      el.setAttribute(naam, waarde);
    }
  }

  function vouwSwitches(wortel) {
    wortel.querySelectorAll('details.facets__panel').forEach((paneel) => {
      const rijen = [...paneel.querySelectorAll('.facets__inputs-list-item')];
      const vakjes = rijen
        .map((rij) => rij.querySelector('.checkbox__input'))
        .filter(Boolean);
      if (!vakjes.length) return;

      const labelVan = (vakje) =>
        (vakje.dataset.label || '').trim().toLowerCase();

      /* Zowel {Ja, Nee} als alleen {Ja} wordt één switch. Shopify toont een
         waarde niet als geen enkel product hem heeft, dus een ja/nee-metafield
         komt soms met maar één van de twee binnen. */
      const ja = vakjes.find((v) => JA.has(labelVan(v)));
      const rest = vakjes.filter((v) => v !== ja);
      const isJaNee = !!ja && rest.every((v) => NEE.has(labelVan(v)));

      /* Staat 'Nee' aan via de URL, dan blijft de groep gewoon een lijst.
         Anders zou dat filter actief zijn zonder dat je het nog ziet. */
      const neeActief = rest.some((v) => v.checked);

      if (!isJaNee || neeActief) {
        zetAttr(paneel, 'data-el-switch', 'lijst');
        rijen.forEach((rij) => zetAttr(rij, 'hidden', null));
        const bewaard = paneel.querySelector('[data-el-oud-label]');
        if (bewaard) {
          bewaard.textContent = bewaard.dataset.elOudLabel;
          bewaard.removeAttribute('data-el-oud-label');
        }
        return;
      }

      const naam = paneel.querySelector('.facets__label')?.textContent.trim();
      const tekst = ja.nextElementSibling?.querySelector('.checkbox__label-text');
      if (naam && tekst && tekst.textContent.trim() !== naam) {
        if (!tekst.dataset.elOudLabel) {
          tekst.dataset.elOudLabel = tekst.textContent.trim();
        }
        tekst.textContent = naam;
      }

      rijen.forEach((rij) => zetAttr(rij, 'hidden', rij.contains(ja) ? null : ''));

      zetAttr(paneel, 'data-el-switch', 'solo');
      if (!paneel.open) paneel.open = true;
    });
  }

  /* --- Prijsslider -------------------------------------------------------- */

  function bouwSlider(component) {
    if (component.dataset.elPrijsKlaar) return;

    const minVeld = component.querySelector('[ref="minInput"]');
    const maxVeld = component.querySelector('[ref="maxInput"]');
    if (!minVeld || !maxVeld) return;

    const paneel = component.closest('details.facets__panel');

    /* Kost alle producten in de collectie hetzelfde, dan is de ondergrens
       gelijk aan de bovengrens en valt er niets te kiezen. Een slider van 0
       tot 0 heeft geen zin, en de twee kale velden eronder laten staan is
       lelijk én nutteloos. Dus het hele paneel weg. */
    const onder = naarGetal(minVeld.dataset.min ?? minVeld.placeholder);
    const boven = naarGetal(maxVeld.dataset.max ?? maxVeld.placeholder);
    if (!Number.isFinite(onder) || !Number.isFinite(boven) || boven <= onder) {
      zetAttr(paneel || component, 'data-el-prijs', 'leeg');
      return;
    }
    zetAttr(paneel || component, 'data-el-prijs', null);

    const komma = String(maxVeld.dataset.max || '').includes(',');
    const munt = (component.dataset.moneyFormat || '').split('{{')[0].trim() || '';
    const stap = stapVoor(boven - onder);

    /* De lege on:change op de twee sliders is geen vergissing. Horizon
       luistert op document naar elke change en stuurt hem naar het dichtste
       element met on:change, hier price-facet-component. Zonder dit gaat de
       change van een slider bij het loslaten meteen door naar Horizon, met
       de oude waarden nog in de velden: een herrender voor niets, een extra
       stap in de geschiedenis, en de duim springt even terug. Staat on:change
       op de slider zelf, dan stopt Horizon daar, want er hangt geen methode
       aan. De velden en de change zelf regelt naarVelden hieronder. */
    const baan = document.createElement('div');
    baan.className = 'el-prijs';
    baan.innerHTML = `
      <div class="el-prijs__baan">
        <div class="el-prijs__spoor"></div>
        <div class="el-prijs__bereik" data-el-bereik></div>
        <input type="range" class="el-prijs__range" data-el-range="min" on:change="">
        <input type="range" class="el-prijs__range" data-el-range="max" on:change="">
      </div>
      <div class="el-prijs__waarden">
        <span data-el-toon="min"></span>
        <span data-el-toon="max"></span>
      </div>`;

    const rMin = baan.querySelector('[data-el-range="min"]');
    const rMax = baan.querySelector('[data-el-range="max"]');
    const balk = baan.querySelector('[data-el-bereik]');
    const tMin = baan.querySelector('[data-el-toon="min"]');
    const tMax = baan.querySelector('[data-el-toon="max"]');

    [rMin, rMax].forEach((r) => {
      r.min = String(onder);
      r.max = String(boven);
      r.step = String(stap);
    });
    rMin.setAttribute('aria-label', minVeld.getAttribute('aria-label') || TEKST.lagePrijs);
    rMax.setAttribute('aria-label', maxVeld.getAttribute('aria-label') || TEKST.hogePrijs);

    const toon = () => {
      const a = parseFloat(rMin.value);
      const b = parseFloat(rMax.value);
      const links = ((a - onder) / (boven - onder)) * 100;
      const rechts = ((b - onder) / (boven - onder)) * 100;
      balk.style.insetInlineStart = links + '%';
      balk.style.insetInlineEnd = 100 - rechts + '%';
      tMin.textContent = munt + naarTekst(a, komma);
      tMax.textContent = munt + naarTekst(b, komma);
      /* Aan de uiteindes liggen de twee duimen over elkaar en is de onderste
         niet meer te pakken. Telkens de duim die daar hoort bovenop leggen. */
      rMin.style.zIndex = a > boven - (boven - onder) / 10 ? '5' : '4';
      rMax.style.zIndex = b < onder + (boven - onder) / 10 ? '5' : '3';
    };

    const uitVelden = () => {
      const a = naarGetal(minVeld.value);
      const b = naarGetal(maxVeld.value);
      rMin.value = String(Number.isFinite(a) ? Math.max(onder, Math.min(a, boven)) : onder);
      rMax.value = String(Number.isFinite(b) ? Math.min(boven, Math.max(b, onder)) : boven);
      if (parseFloat(rMin.value) > parseFloat(rMax.value)) rMin.value = rMax.value;
      toon();
    };

    /* Slepen geeft één change bij het loslaten, maar met de pijltjestoetsen
       vuurt er één per aanslag. Zonder rem stuurt iemand die van 10 naar 5
       tikt vijftig keer een herrender de deur uit. */
    let remmer;
    const naarVelden = () => {
      clearTimeout(remmer);
      remmer = setTimeout(() => {
        const a = parseFloat(rMin.value);
        const b = parseFloat(rMax.value);
        minVeld.value = a <= onder ? '' : naarVeldWaarde(a);
        maxVeld.value = b >= boven ? '' : naarVeldWaarde(b);
        /* Eén change is genoeg: Horizon leest bij elke change beide velden
           uit. Twee changes gaven twee herrenders en twee keer dezelfde stap
           in de geschiedenis, dus twee keer terug om terug te gaan. */
        maxVeld.dispatchEvent(new Event('change', { bubbles: true }));
      }, 250);
    };

    rMin.addEventListener('input', () => {
      if (parseFloat(rMin.value) > parseFloat(rMax.value)) rMin.value = rMax.value;
      toon();
    });
    rMax.addEventListener('input', () => {
      if (parseFloat(rMax.value) < parseFloat(rMin.value)) rMax.value = rMin.value;
      toon();
    });
    rMin.addEventListener('change', naarVelden);
    rMax.addEventListener('change', naarVelden);

    /* Horizon vult de velden na een paginaload met de komma-notatie (5,00).
       Laat je dat staan, dan sneuvelt het prijsfilter zodra de bezoeker een
       ander filter aanraakt: die komma gaat mee de URL in en Shopify negeert
       hem. Hier stil rechtzetten, zonder change, anders herlaadt de sectie. */
    [minVeld, maxVeld].forEach((veld) => {
      const getal = naarGetal(veld.value);
      if (Number.isFinite(getal)) veld.value = naarVeldWaarde(getal);
    });

    component.appendChild(baan);
    component.dataset.elPrijsKlaar = '1';
    uitVelden();
  }

  /* --- Opstarten en bijhouden --------------------------------------------- */

  function pasToe(wortel = document) {
    vouwSwitches(wortel);
    wortel.querySelectorAll('price-facet-component').forEach(bouwSlider);
  }

  /* Aan de achterkant van de wijzigingen wachten, niet aan de voorkant. Een
     herrender komt in meerdere stukken binnen; reageer je op het eerste stuk,
     dan draai je over een halve DOM en komt er niets meer achteraan. */
  let timer;
  function plan() {
    clearTimeout(timer);
    timer = setTimeout(pasToe, 80);
  }

  /* Horizon hertekent de filters via de Section Rendering API na elke
     wijziging, en de mobiele lade komt pas later in de DOM. Beide keren is
     onze opmaak weg, dus kijken we mee en passen we opnieuw toe. pasToe is
     idempotent, herhalen kost niets.

     Kijken naar documentElement en niet naar body: Horizon vervangt bij een
     herrender het body-element zelf, en een observer die aan het oude body
     hing krijgt daarna niets meer door. Dat kost je de opmaak vanaf het
     eerste filter dat de bezoeker aanraakt. */
  function start() {
    pasToe();
    new MutationObserver(plan).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();